import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { PHONE, MOBILE, TABLET, REC_PANEL_BREAKPOINT } from "../util/Constants";

export const ScreenContext = createContext({
  isPhone: false,
  isMobile: false,
  isTablet: false,
  isCompactDesktop: false,
  windowWidth: typeof window !== "undefined" ? window.innerWidth : 1024,
});

const canMatchMedia =
  typeof window !== "undefined" && typeof window.matchMedia === "function";

function getInitial(breakpoint) {
  return canMatchMedia && window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
}

function useBreakpoint(breakpoint) {
  const [matches, setMatches] = useState(() => getInitial(breakpoint));

  useEffect(() => {
    if (!canMatchMedia) return;
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = (e) => setMatches(e.matches);

    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
    } else {
      mql.addListener(onChange);
    }

    setMatches(mql.matches);

    return () => {
      if (typeof mql.removeEventListener === "function") {
        mql.removeEventListener("change", onChange);
      } else {
        mql.removeListener(onChange);
      }
    };
  }, [breakpoint]);

  return matches;
}

export function ScreenProvider({ children }) {
  const isPhone = useBreakpoint(PHONE);
  const isMobile = useBreakpoint(MOBILE);
  const isTablet = useBreakpoint(TABLET);
  const isCompactDesktop = useBreakpoint(REC_PANEL_BREAKPOINT);

  const [windowWidth, setWindowWidth] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth : 1024)
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const value = useMemo(() => ({
    isPhone,
    isMobile,
    isTablet,
    isCompactDesktop,
    windowWidth,
  }), [isPhone, isMobile, isTablet, isCompactDesktop, windowWidth]);

  return (
    <ScreenContext.Provider value={value}>
      {children}
    </ScreenContext.Provider>
  );
}

export const useScreen = () => useContext(ScreenContext);
